import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, unref, type MaybeRef } from "vue";

import { ok } from "@/lib/openapi";
import { findDataResults, findStaticResults } from "@/search/executor";
import { useStore as useScaffoldingStore } from "@/store/stores/scaffolding";
import type { SearchResult, SearchResultWithId } from "@/types/search_result";

import internalApi from "../../internal-base";
import { useNewsletterId } from "../utils";

const MAX_BRIEF_ITEMS = 15;

export const useSearchQuery = ({
  q: qRef,
  scopes: scopesRef = [],
  brief = false,
}: {
  q: MaybeRef<string>;
  scopes?: MaybeRef<string[]>;
  brief?: boolean;
}) => {
  const newsletterId = useNewsletterId();
  const queryClient = useQueryClient();

  const scaffoldingStore = useScaffoldingStore();

  const normalizedQuery = computed(() => {
    return (
      unref(qRef)
        // trim leading, trailing and repetitive whitespace
        .replace(/^ +|[ .,@]+$| +(?= )/g, "")
        // lowercase everything
        .toLowerCase()
    );
  });

  const query = useQuery({
    queryKey: [
      newsletterId,
      "search",
      { q: normalizedQuery, scopes: scopesRef, brief },
    ],
    enabled: () => normalizedQuery.value.length !== 0,
    async queryFn({ signal }): Promise<SearchResultWithId[]> {
      const scopes = unref(scopesRef);

      const data = await ok(
        internalApi.post("/search", {
          signal,
          body: {
            text: normalizedQuery.value,
            scopes: scopes.length !== 0 ? scopes : undefined,
          },
        })
      );

      let onlineResults = data.results as unknown as SearchResult[];
      if (brief) {
        onlineResults = onlineResults.slice(0, MAX_BRIEF_ITEMS);
      }

      // Merge online data results with static results (pages, integrations)
      const staticResults = findStaticResults({
        q: normalizedQuery.value,
        permissions: scaffoldingStore.currentPermissions,
        scopes: scopes,
      });

      const allResults = [...onlineResults, ...staticResults];

      return allResults.map((result) => ({
        id: `${result.type}:${result.obj.id}`,
        ...result,
      }));
    },
    initialDataUpdatedAt: 0,
    initialData(): SearchResultWithId[] {
      const query = normalizedQuery.value;
      if (query === "") {
        return [];
      }

      const newsletter = scaffoldingStore.newsletter;
      if (!newsletter) {
        return [];
      }

      const scopes = unref(scopesRef);

      let dataResults = findDataResults({
        q: query,
        newsletter: newsletter,
        queryClient: queryClient,
        scopes: scopes,
      });
      if (brief) {
        dataResults = dataResults.slice(0, MAX_BRIEF_ITEMS);
      }

      const staticResults = findStaticResults({
        q: query,
        permissions: scaffoldingStore.currentPermissions,
        scopes: scopes,
      });

      const results = [...dataResults, ...staticResults];

      return results.map((result) => ({
        id: `${result.type}:${result.obj.id}`,
        ...result,
      }));
    },
  });

  return query;
};
